import React, { useState } from "react";
import {
  Box,
  Button,
  Stepper,
  Step,
  StepButton,
  Typography,
  CircularProgress,
  Backdrop,
} from "@mui/material";
import InspectionInfo from "../component/project_sub_comp/inspectionInfo";
import PVCCheckList from "../component/project_sub_comp/pvcCheckList";
import PVICheckList from "../component/project_sub_comp/pviCheckList";
import AFECheckList from "../component/project_sub_comp/afeCheckList";
import PVATestReport from "../component/project_sub_comp/pvaTestReport";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import {
  updateFormField,
  updateStep,
} from "../../store/slices/inspection_info.slices";
import { panels } from "../data/staticData";
import {
  validateInspectionInfo,
  validatePVA,
} from "../component/project_sub_comp/function";

export default function Project() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const [stepErrors, setStepErrors] = useState({});

  const { selectedInspectorId, formData, activeStep } = useSelector(
    (state) => state.ins_info
  );

  const isEditMode = location.pathname.includes("edit");

  const { invertersList, selectedPanel } = useSelector(
    (state) => state.pva_test
  );

  // const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);

  const steps = [
    "Inspection Info",
    "PVA Test Report",
    "PVC Checklist",
    "PVI Checklist",
    "AFE Checklist",
  ];

  // 🟢 Step Change With Loader
  const changeStep = (newStep) => {
    setLoading(true);

    setTimeout(() => {
      dispatch(updateStep(newStep)); // 🔥 use redux
      window.scrollTo({ top: 0, behavior: "smooth" });
      setLoading(false);
    }, 600);
  };

  const validateStep = (step) => {
    switch (step) {
      case 0:
        return validateInspectionInfo({
          formData,
          selectedInspectorId,
          setStepErrors,
        });
      case 1:
        return validatePVA({ selectedPanel, invertersList, setStepErrors });
      default:
        return true;
    }
  };

  const handleNext = () => {
    const isValid = validateStep(activeStep);

    if (!isValid) return alert("fill all the required fields");

    changeStep(activeStep + 1);
  };

  const handleBack = () => {
    changeStep(activeStep - 1);
  };

  const handleStepClick = (stepIndex) => {
    changeStep(stepIndex);
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return <InspectionInfo errors={stepErrors} />;
      case 1:
        return <PVATestReport errors={stepErrors} />;
      case 2:
        return <PVCCheckList />;
      case 3:
        return <PVICheckList />;
      case 4:
        return <AFECheckList />;
      default:
        return <InspectionInfo errors={stepErrors} />;
    }
  };

  const calculatePower = () => {
    if (!selectedPanel) {
      alert("⚠️ Panel selection required before calculation!");
      return false;
    }

    const panel = panels.find((item) => item.name === selectedPanel);
    const pMax = parseFloat(panel?.pMax);

    if (isNaN(pMax) || pMax <= 0) {
      alert("⚠️ Invalid panel data.");
      return false;
    }

    invertersList.forEach((inverter) => {
      let totalPanels = 0;

      inverter.mppts.forEach((mppt) => {
        mppt.strings.forEach((str) => {
          totalPanels += parseFloat(str.quantity) || 0;
        });
      });

      const power = ((totalPanels * pMax) / 1000).toFixed(2);
      const estimatedPerformance = (power * 800).toFixed(0);

      dispatch(updateFormField({ field: "power", value: `${power}` }));
      dispatch(
        updateFormField({
          field: "estimatedPerformance",
          value: `${estimatedPerformance}`,
        })
      );
    });

    return true;
  };

  const handlePreview = () => {
    if (calculatePower()) {
      navigate("/preview");
    }
  };

  return (
    <Box className="cp_cont" sx={{ width: "100%" }}>
      {/* 🟢 PAGE HEADER */}
      <Typography className="cp_title">PV INSPECTION</Typography>

      {/* 🟢 STEPPER WITHOUT LABELS */}
      <Stepper className="cp_step" activeStep={activeStep} sx={{ mb: 4 }}>
        {steps.map((_, index) => (
          <Step key={index}>
            <StepButton onClick={() => handleStepClick(index)} />
          </Step>
        ))}
      </Stepper>

      {/* 🟢 CONTENT */}
      <Box className="cp_contents">
        <Typography className="title">{steps[activeStep]}</Typography>

        {renderStepContent(activeStep)}
      </Box>

      {/* 🟢 NAVIGATION BUTTONS */}
      <Box
        sx={{
          margin: "15px",
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <Button
          variant="outlined"
          disabled={activeStep === 0}
          onClick={handleBack}
        >
          Back
        </Button>

        {activeStep < steps.length - 1 ? (
          <Button variant="contained" onClick={handleNext}>
            Next
          </Button>
        ) : (
          <Button variant="outlined" color="secondary" onClick={handlePreview}>
            Preview Reports
          </Button>
        )}
      </Box>

      {/* 🟢 GLOBAL LOADER */}
      <Backdrop open={loading} sx={{ zIndex: 1300 }}>
        <CircularProgress color="inherit" />
      </Backdrop>
    </Box>
  );
}
